import { useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { LuCheck, LuUserPlus } from "react-icons/lu";
import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  Field,
  Heading,
  HStack,
  NativeSelect,
  Portal,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { FieldSkeleton } from "../components/LoadingSkeletons";
import { TableSkeleton } from "../components/TableSkeleton";
import { MultiSelectTags } from "../components/MultiSelectTags";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { readableError } from "@/lib/errors";
import type { PersonOption } from "./ActivityAdminShared";
import {
  participationRoleLabel,
  participationStateColor,
  participationStateLabel,
} from "./eventsShared";

/**
 * Who is coming, and who came.
 *
 * The admin's half of an occasion, and the reason batch confirmation exists:
 * forty attendances recorded in one pass, and -- through the satisfaction link
 * -- forty steps completed without a single activity being opened.
 *
 * Capacity is deliberately not enforced here. It binds at sign-up and at
 * promotion, where it is a decision about who may have a place; this is a
 * record of who was actually in the room, and a room that held one more person
 * than planned is a fact rather than an error.
 */
export function EventRoster({ eventId, cancelled }: { eventId: Id<"events">; cancelled: boolean }) {
  const roster = useQuery(api.participations.listForEvent, { eventId });
  const confirmRoster = useMutation(api.participations.confirmRoster);
  const setState = useMutation(api.participations.setParticipationState);

  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const coming = useMemo(
    () => (roster ?? []).filter((row) => row.state !== "cancelled"),
    [roster],
  );
  const unconfirmed = useMemo(
    () => coming.filter((row) => row.state === "planned" || row.state === "waitlisted"),
    [coming],
  );

  if (roster === undefined) {
    return <TableSkeleton label="Loading the roster" rows={6} columns={4} />;
  }

  async function confirmTicked() {
    setBusy(true);
    try {
      const personIds = coming
        .filter((row) => ticked.has(String(row._id)))
        .map((row) => row.personId);
      const result = await confirmRoster({ eventId, personIds, role: "attendee" });
      toastSuccess(
        `${result.confirmed} confirmed`,
        result.completed === 0
          ? undefined
          : `${result.completed} ${result.completed === 1 ? "step" : "steps"} completed by it.`,
      );
      setTicked(new Set());
    } catch (error) {
      const { message, detail } = readableError(error, "The roster could not be confirmed");
      toastError(message, detail);
    } finally {
      setBusy(false);
    }
  }

  async function changeState(participationId: Id<"participations">, state: string) {
    try {
      const result = await setState({ participationId, state: state as never });
      toastSuccess(
        "Updated",
        result.promoted === null ? undefined : "Somebody on the waitlist has taken the free place.",
      );
    } catch (error) {
      const { message, detail } = readableError(error, "That could not be changed");
      toastError(message, detail);
    }
  }

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body p={5}>
        <Stack gap={4}>
          <HStack justify="space-between" align="end" wrap="wrap" gap={3}>
            <Box>
              <Heading size="md">Roster</Heading>
              <Text mt={1} fontSize="sm" color="fg.muted">
                Only visible to admins. Confirming attendance also completes activity steps that need it.
              </Text>
            </Box>
            {cancelled ? null : (
              <HStack gap={2}>
                <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
                  <LuUserPlus />Add people
                </Button>
                <Button
                  size="sm"
                  colorPalette="blue"
                  loading={busy}
                  disabled={ticked.size === 0}
                  onClick={() => void confirmTicked()}
                >
                  <LuCheck />Confirm {ticked.size === 0 ? "" : ticked.size}
                </Button>
              </HStack>
            )}
          </HStack>

          {roster.length === 0 ? (
            <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
              <Text fontSize="sm" color="fg.muted">Nobody is down for this yet.</Text>
            </Box>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader w="2.5rem">
                      {cancelled ? null : (
                        <Checkbox.Root
                          size="sm"
                          checked={ticked.size > 0 && ticked.size === unconfirmed.length}
                          onCheckedChange={(e) =>
                            setTicked(e.checked ? new Set(unconfirmed.map((row) => String(row._id))) : new Set())}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      )}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>Who</Table.ColumnHeader>
                    <Table.ColumnHeader>As</Table.ColumnHeader>
                    <Table.ColumnHeader>State</Table.ColumnHeader>
                    <Table.ColumnHeader />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {roster.map((row) => (
                    <Table.Row key={row._id} opacity={row.state === "cancelled" ? 0.5 : 1}>
                      <Table.Cell>
                        {cancelled || row.state === "cancelled" || row.state === "confirmed" ? null : (
                          <Checkbox.Root
                            size="sm"
                            checked={ticked.has(String(row._id))}
                            onCheckedChange={(e) =>
                              setTicked((current) => {
                                const next = new Set(current);
                                if (e.checked) {
                                  next.add(String(row._id));
                                } else {
                                  next.delete(String(row._id));
                                }
                                return next;
                              })}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                          </Checkbox.Root>
                        )}
                      </Table.Cell>
                      <Table.Cell>{row.personName}</Table.Cell>
                      <Table.Cell>{participationRoleLabel(row.role)}</Table.Cell>
                      <Table.Cell>
                        <Badge colorPalette={participationStateColor(row.state)}>
                          {participationStateLabel(row.state)}
                          {row.waitlistPosition === null ? "" : ` · ${row.waitlistPosition}`}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        {cancelled ? null : (
                          <NativeSelect.Root size="xs" maxW="10rem">
                            <NativeSelect.Field
                              value=""
                              onChange={(e) => {
                                if (e.target.value !== "") {
                                  void changeState(row._id, e.target.value);
                                }
                              }}
                            >
                              <option value="">Change…</option>
                              <option value="confirmed">Took part</option>
                              <option value="planned">Coming</option>
                              <option value="noShow">Did not come</option>
                              <option value="cancelled">Withdrawn</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Stack>
      </Card.Body>

      <AddPeopleDialog
        eventId={eventId}
        open={adding}
        onClose={() => setAdding(false)}
        alreadyOn={new Set(coming.map((row) => String(row.personId)))}
      />
    </Card.Root>
  );
}

/**
 * Put somebody on the roster who did not sign up: a scheduled speaker, a host,
 * or an attendance being recorded on their behalf.
 *
 * The person need not have an account, which is how an external speaker is
 * handled -- they are an ordinary person row created in the directory, and
 * nothing here asks whether they can sign in.
 */
function AddPeopleDialog({
  eventId,
  open,
  onClose,
  alreadyOn,
}: {
  eventId: Id<"events">;
  open: boolean;
  onClose: () => void;
  alreadyOn: Set<string>;
}) {
  const directory = useQuery(api.persons.listCeMMies, open ? {} : "skip");
  const schedule = useMutation(api.participations.scheduleIntoSlot);
  const [selected, setSelected] = useState<string[]>([]);
  const [role, setRole] = useState("attendee");
  const [saving, setSaving] = useState(false);

  const persons: PersonOption[] | undefined = directory?.persons;

  async function save() {
    setSaving(true);
    let added = 0;
    try {
      for (const personId of selected) {
        await schedule({
          eventId,
          personId: personId as Id<"persons">,
          role: role as never,
          note: "",
        });
        added += 1;
      }
      toastSuccess(`${added} added`, "They have been told.");
      setSelected([]);
      onClose();
    } catch (error) {
      const { message, detail } = readableError(
        error,
        added === 0 ? "Nobody could be added" : `Only ${added} could be added`,
      );
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }} size="lg">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>Add people to this event</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Field.Root>
                  <Field.Label>As what</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field value={role} onChange={(e) => setRole(e.target.value)}>
                      <option value="attendee">Attendee</option>
                      <option value="speaker">Speaker</option>
                      <option value="invitedLecturer">Invited lecturer</option>
                      <option value="posterPresenter">Poster</option>
                      <option value="host">Host</option>
                      <option value="organiser">Organiser</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                {persons === undefined ? (
                  <FieldSkeleton label="Loading people" height="16" />
                ) : (
                  <MultiSelectTags
                    label="Who"
                    placeholder="Search by name or address…"
                    emptyMessage="Nobody matches that."
                    helperText="Somebody already on the list will have their part changed rather than added twice."
                    options={persons.map((person) => ({
                      value: String(person._id),
                      label: person.fullName + (alreadyOn.has(String(person._id)) ? " — already on the list" : ""),
                      hint: person.email ?? undefined,
                    }))}
                    selected={selected}
                    onChange={setSelected}
                  />
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                colorPalette="blue"
                loading={saving}
                disabled={selected.length === 0}
                onClick={() => void save()}
              >
                Add {selected.length === 0 ? "" : selected.length}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
